/**
 * Admin Promotions UI (US-04-09)
 * List + create/edit/deactivate time-bounded promotions that override base pricing.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Trash2, Edit2, X, AlertCircle, Check } from 'lucide-react';

const WORKERS_URL = import.meta.env.VITE_WORKERS_URL || 'http://localhost:8787';

type PromotionType = 'global' | 'per_user' | 'per_pack' | 'first_time' | 'welcome_credits';

interface Promotion {
  id: string;
  name: string;
  description: string | null;
  type: PromotionType;
  rule: {
    // US-04-10 new cost-based rule fields
    markup_override?: number;
    markup_discount_percent?: number;
    agent_output_ratio_override?: Record<string, number>;
    chargeable_override?: Record<string, boolean>;
    vnd_per_credit_override?: number;
    welcome_credits_amount?: number;
    // Legacy fields (for reading old data, no longer written)
    multiplier_override?: Record<string, number>;
    discount_percent?: number;
  };
  conditions: Record<string, any>;
  active_from: string;
  active_to: string | null;
  is_active: boolean;
  priority: number;
  created_at: string;
}

interface PromotionForm {
  name: string;
  description: string;
  type: PromotionType;
  discount_percent: string;
  multiplier_translate: string;
  multiplier_qc: string;
  multiplier_audit: string;
  welcome_credits_amount: string;  // for type='welcome_credits'
  active_from: string;
  active_to: string;
  priority: string;
  is_active: boolean;
}

const EMPTY_FORM: PromotionForm = {
  name: '',
  description: '',
  type: 'global',
  discount_percent: '',
  multiplier_translate: '',
  multiplier_qc: '',
  multiplier_audit: '',
  welcome_credits_amount: '',
  active_from: new Date().toISOString().slice(0, 16),
  active_to: '',
  priority: '0',
  is_active: true,
};

const PromotionsAdmin: React.FC = () => {
  const { session } = useAuth();
  const [promos, setPromos] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PromotionForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const token = session?.access_token;

  const fetchPromos = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${WORKERS_URL}/api/admin/promotions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = (await res.json()) as { promotions: Promotion[] };
      setPromos(data.promotions);
    } catch (err: any) {
      setToast({ type: 'error', msg: `Load fail: ${err.message}` });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPromos();
  }, [fetchPromos]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const openNewForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const openEditForm = (p: Promotion) => {
    setForm({
      name: p.name,
      description: p.description || '',
      type: p.type,
      discount_percent: p.rule.markup_discount_percent != null ? String(p.rule.markup_discount_percent) : (p.rule.discount_percent != null ? String(p.rule.discount_percent) : ''),
      welcome_credits_amount: p.rule.welcome_credits_amount != null ? String(p.rule.welcome_credits_amount) : '',
      // US-04-10: prefer new field, fall back to legacy multiplier_override
      multiplier_translate: p.rule.agent_output_ratio_override?.translate != null ? String(p.rule.agent_output_ratio_override.translate) : (p.rule.multiplier_override?.translate != null ? String(p.rule.multiplier_override.translate) : ''),
      multiplier_qc: p.rule.agent_output_ratio_override?.qc != null ? String(p.rule.agent_output_ratio_override.qc) : (p.rule.multiplier_override?.qc != null ? String(p.rule.multiplier_override.qc) : ''),
      multiplier_audit: p.rule.agent_output_ratio_override?.audit != null ? String(p.rule.agent_output_ratio_override.audit) : (p.rule.multiplier_override?.audit != null ? String(p.rule.multiplier_override.audit) : ''),
      active_from: p.active_from.slice(0, 16),
      active_to: p.active_to ? p.active_to.slice(0, 16) : '',
      priority: String(p.priority),
      is_active: p.is_active,
    });
    setEditingId(p.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!token) return;
    if (!form.name.trim()) {
      setToast({ type: 'error', msg: 'Name required' });
      return;
    }

    const rule: any = {};

    if (form.type === 'welcome_credits') {
      // Welcome credits promo: only accepts welcome_credits_amount
      if (!form.welcome_credits_amount) {
        setToast({ type: 'error', msg: 'Welcome credits: cần nhập số credits' });
        return;
      }
      const n = parseInt(form.welcome_credits_amount);
      if (isNaN(n) || n <= 0) {
        setToast({ type: 'error', msg: 'Welcome credits phải > 0' });
        return;
      }
      rule.welcome_credits_amount = n;
    } else {
      // US-04-10 cost-based promo rule:
      //   discount_percent → markup_discount_percent (giảm giá bán)
      //   multiplier_X → agent_output_ratio_override (modify est output per agent — approximation)
      if (form.discount_percent) {
        const n = parseFloat(form.discount_percent);
        if (!isNaN(n)) rule.markup_discount_percent = n;
      }
      const outputRatioOverride: Record<string, number> = {};
      if (form.multiplier_translate) {
        const n = parseFloat(form.multiplier_translate);
        if (!isNaN(n)) outputRatioOverride.translate = n;
      }
      if (form.multiplier_qc) {
        const n = parseFloat(form.multiplier_qc);
        if (!isNaN(n)) outputRatioOverride.qc = n;
      }
      if (form.multiplier_audit) {
        const n = parseFloat(form.multiplier_audit);
        if (!isNaN(n)) outputRatioOverride.audit = n;
      }
      if (Object.keys(outputRatioOverride).length > 0) rule.agent_output_ratio_override = outputRatioOverride;

      if (Object.keys(rule).length === 0) {
        setToast({ type: 'error', msg: 'Rule empty: set discount_percent hoặc output ratio override' });
        return;
      }
    }

    const payload = {
      name: form.name,
      description: form.description,
      type: form.type,
      rule,
      active_from: form.active_from ? new Date(form.active_from).toISOString() : new Date().toISOString(),
      active_to: form.active_to ? new Date(form.active_to).toISOString() : null,
      priority: parseInt(form.priority) || 0,
      is_active: form.is_active,
    };

    setSaving(true);
    try {
      const url = editingId
        ? `${WORKERS_URL}/api/admin/promotions/${editingId}`
        : `${WORKERS_URL}/api/admin/promotions`;
      const res = await fetch(url, {
        method: editingId ? 'PATCH' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json() as { success?: boolean; error?: string; promotion?: Promotion };
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setToast({ type: 'success', msg: editingId ? 'Updated!' : 'Created!' });
      setShowForm(false);
      await fetchPromos();
    } catch (err: any) {
      setToast({ type: 'error', msg: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!token || !window.confirm('Deactivate promotion này?')) return;
    try {
      const res = await fetch(`${WORKERS_URL}/api/admin/promotions/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setToast({ type: 'success', msg: 'Deactivated' });
      await fetchPromos();
    } catch (err: any) {
      setToast({ type: 'error', msg: err.message });
    }
  };

  const isCurrentlyActive = (p: Promotion): boolean => {
    if (!p.is_active) return false;
    const now = new Date();
    if (new Date(p.active_from) > now) return false;
    if (p.active_to && new Date(p.active_to) < now) return false;
    return true;
  };

  const summarizeRule = (p: Promotion): string => {
    const parts: string[] = [];
    if (p.rule.discount_percent) parts.push(`-${p.rule.discount_percent}% all`);
    if (p.rule.multiplier_override) {
      for (const [k, v] of Object.entries(p.rule.multiplier_override)) {
        parts.push(`${k}×${v}`);
      }
    }
    return parts.join(', ') || '(empty)';
  };

  if (loading) return <div className="p-6 text-slate-500">Loading promotions...</div>;

  return (
    <div className="h-full overflow-auto bg-slate-50">
      <div className="max-w-5xl mx-auto p-6 space-y-4">
        {toast && (
          <div
            className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 ${
              toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
            }`}
          >
            {toast.type === 'success' ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            <span className="text-sm">{toast.msg}</span>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-slate-800">🎁 Promotions</h2>
              <p className="text-xs text-slate-500">Time-bounded / conditional overrides lên base pricing</p>
            </div>
            <button
              onClick={openNewForm}
              className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              New Promotion
            </button>
          </div>

          {promos.length === 0 ? (
            <div className="text-sm text-slate-400 italic py-6 text-center">
              Chưa có promotion nào. Click "New" để tạo.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs font-normal text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="px-2 py-1.5 text-left">Name</th>
                  <th className="px-2 py-1.5 text-left">Type</th>
                  <th className="px-2 py-1.5 text-left">Rule</th>
                  <th className="px-2 py-1.5 text-left">Active window</th>
                  <th className="px-2 py-1.5 text-left">Priority</th>
                  <th className="px-2 py-1.5 text-left">Status</th>
                  <th className="px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {promos.map((p) => {
                  const live = isCurrentlyActive(p);
                  return (
                    <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-2 py-2">
                        <div className="font-medium text-slate-800">{p.name}</div>
                        {p.description && <div className="text-[10px] text-slate-500">{p.description}</div>}
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-600">{p.type}</td>
                      <td className="px-2 py-2 text-xs font-mono text-slate-700">{summarizeRule(p)}</td>
                      <td className="px-2 py-2 text-[10px] text-slate-500">
                        <div>{new Date(p.active_from).toLocaleDateString('vi-VN')}</div>
                        <div>→ {p.active_to ? new Date(p.active_to).toLocaleDateString('vi-VN') : '∞'}</div>
                      </td>
                      <td className="px-2 py-2 text-center text-xs">{p.priority}</td>
                      <td className="px-2 py-2">
                        {live ? (
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold uppercase">LIVE</span>
                        ) : p.is_active ? (
                          <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold uppercase">scheduled/past</span>
                        ) : (
                          <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-bold uppercase">off</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button
                          onClick={() => openEditForm(p)}
                          className="p-1 hover:bg-slate-200 rounded mr-1"
                          title="Edit"
                        >
                          <Edit2 className="w-3.5 h-3.5 text-slate-600" />
                        </button>
                        {p.is_active && (
                          <button
                            onClick={() => handleDeactivate(p.id)}
                            className="p-1 hover:bg-red-100 rounded"
                            title="Deactivate"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-600" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Form modal */}
        {showForm && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-bold text-slate-800">{editingId ? 'Edit' : 'New'} Promotion</h3>
                <button onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-5 space-y-3 text-sm">
                <div>
                  <label className="text-[11px] text-slate-500 block mb-1">Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="VD: Flash sale QC -50% Tết 2026"
                    className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 block mb-1">Description</label>
                  <input
                    type="text"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 block mb-1">Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value as PromotionForm['type'] })}
                    className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                  >
                    <option value="global">Global (apply to all users — pricing)</option>
                    <option value="welcome_credits">🎁 Welcome credits (signup bonus)</option>
                    <option value="first_time">First-time user only (pricing)</option>
                    <option value="per_user">Per specific users (defer — need user IDs)</option>
                    <option value="per_pack">Per credit pack (defer)</option>
                  </select>
                </div>

                {form.type === 'welcome_credits' ? (
                  <div className="border-t border-slate-200 pt-3">
                    <div className="text-xs font-bold uppercase text-slate-600 mb-2">Welcome credits rule</div>
                    <div className="mb-2">
                      <label className="text-[11px] text-slate-500 block mb-1">Số credits tặng khi user signup</label>
                      <input
                        type="number"
                        step="1000"
                        min="0"
                        value={form.welcome_credits_amount}
                        onChange={(e) => setForm({ ...form, welcome_credits_amount: e.target.value })}
                        placeholder="VD: 200000 (200K credits ≈ 1 báo cáo dày 25 trang)"
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm font-mono"
                      />
                    </div>
                    <div className="text-[10px] text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
                      ℹ️ Chỉ 1 promo welcome_credits active tại 1 thời điểm (cao priority nhất thắng). User mới tạo account sẽ được cộng credits theo config này qua trigger <code>handle_new_profile_token</code>.
                    </div>
                  </div>
                ) : (
                  <div className="border-t border-slate-200 pt-3">
                    <div className="text-xs font-bold uppercase text-slate-600 mb-2">Rule pricing (chọn 1 hoặc kết hợp)</div>

                    <div className="mb-3">
                      <label className="text-[11px] text-slate-500 block mb-1">Discount % (all multipliers)</label>
                      <input
                        type="number"
                        step="1"
                        min="0"
                        max="100"
                        value={form.discount_percent}
                        onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                        placeholder="VD: 50 = giảm 50%"
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm font-mono"
                      />
                    </div>

                    <div className="text-[11px] text-slate-500 mb-1">Hoặc override multiplier cụ thể:</div>
                    <div className="grid grid-cols-3 gap-2">
                      {(['translate', 'qc', 'audit'] as const).map((t) => (
                        <div key={t}>
                          <label className="text-[10px] text-slate-500 block mb-0.5">{t}</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="10"
                            value={form[`multiplier_${t}` as keyof PromotionForm] as string}
                            onChange={(e) => setForm({ ...form, [`multiplier_${t}`]: e.target.value } as any)}
                            placeholder="—"
                            className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-mono"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 border-t border-slate-200 pt-3">
                  <div>
                    <label className="text-[11px] text-slate-500 block mb-1">Active from</label>
                    <input
                      type="datetime-local"
                      value={form.active_from}
                      onChange={(e) => setForm({ ...form, active_from: e.target.value })}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-500 block mb-1">Active to (empty = forever)</label>
                    <input
                      type="datetime-local"
                      value={form.active_to}
                      onChange={(e) => setForm({ ...form, active_to: e.target.value })}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-slate-500 block mb-1">Priority (higher = apply first)</label>
                    <input
                      type="number"
                      step="1"
                      value={form.priority}
                      onChange={(e) => setForm({ ...form, priority: e.target.value })}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm font-mono"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-5">
                    <input
                      type="checkbox"
                      id="promo-active"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    />
                    <label htmlFor="promo-active" className="text-sm text-slate-700">Is active (on/off)</label>
                  </div>
                </div>
              </div>
              <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
                <button
                  onClick={() => setShowForm(false)}
                  className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm"
                >
                  Hủy
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm disabled:bg-slate-300"
                >
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromotionsAdmin;
